import React, { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { error: Error | null; isChunk: boolean };

function isChunkLoadError(err: Error): boolean {
  const msg = err.message || '';
  return (
    err.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

/** Catches lazy-route and render errors so the shell stays usable. */
export default class ModuleErrorBoundary extends Component<Props, State> {
  state: State = { error: null, isChunk: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, isChunk: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ModuleErrorBoundary:', error, info.componentStack);
    try {
      const body = JSON.stringify({
        message: error.message,
        stack: error.stack || '',
        componentStack: info.componentStack,
        type: 'module-error-boundary',
        path: window.location.pathname,
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/log-error', new Blob([body], { type: 'application/json' }));
      } else {
        fetch('/api/log-error', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
      }
    } catch {
      /* ignore */
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, isChunk } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="card"
        style={{
          margin: '2rem auto',
          maxWidth: '520px',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ margin: '0 0 0.75rem', color: '#1e293b', fontSize: '1.25rem' }}>
          {isChunk ? 'A new version is available' : 'This page could not load'}
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5, marginBottom: '1.25rem' }}>
          {isChunk
            ? 'Your browser may be using an older copy of the app. Refresh to load the latest build.'
            : 'Something went wrong while opening this module. Try refreshing; if it continues, contact support.'}
        </p>
        {!isChunk && error.message && (
          <pre
            style={{
              textAlign: 'left',
              fontSize: '0.75rem',
              overflow: 'auto',
              maxHeight: '120px',
              background: '#f1f5f9',
              padding: '0.75rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}
          >
            {error.message}
          </pre>
        )}
        <button type="button" className="btn-primary" onClick={this.handleReload}>
          Refresh page
        </button>
      </div>
    );
  }
}